import { Card } from "@/components/ui/card";
import { LoaderIcon } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useQuery } from "@tanstack/react-query";
import { userCreditQueries } from "@/api/user-credits";
import { DateRange } from "@/lib/date";

export function UsageTab({
  username,
  dateRange,
}: {
  username: string;
  dateRange: DateRange;
}) {
  const { data: productUsage, isLoading: isProductUsageLoading } = useQuery(
    userCreditQueries.productUsage(
      username,
      dateRange.from.toISOString(),
      dateRange.to.toISOString(),
    ),
  );

  const usageEntries = Object.entries(productUsage ?? {});
  const totalUsed = usageEntries.reduce((sum, [, usage]) => {
    const c = usage.credits ?? usage.total_credits ?? 0;
    return sum + c;
  }, 0);
  const sortedEntries = [...usageEntries].sort(([, a], [, b]) => {
    const aCredits = a.credits ?? a.total_credits ?? 0;
    const bCredits = b.credits ?? b.total_credits ?? 0;
    return bCredits - aCredits; // descending
  });

  return (
    <>
      {/* Usage Analysis Tab Content */}
      <Card className="p-4 mb-6">
        <h3 className="text-lg font-semibold mb-4">Usage by Product</h3>
        {isProductUsageLoading ? (
          <div className="h-[200px] flex items-center justify-center">
            <LoaderIcon className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead className="text-right">Credits Used</TableHead>
                <TableHead className="text-right">API Calls</TableHead>
                <TableHead className="text-right">% of Total</TableHead>
                <TableHead className="text-right">Avg Credits/Call</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedEntries.map(([product, usage]) => {
                const c = usage.credits ?? usage.total_credits ?? 0;
                const calls = usage.calls ?? usage.total_calls ?? 0;
                // Safely handle if totalUsed=0
                const pctOfTotal = totalUsed > 0 ? (c / totalUsed) * 100 : 0;

                return (
                  <TableRow key={product}>
                    <TableCell className="font-medium">{product}</TableCell>
                    <TableCell className="text-right font-mono">
                      {c.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {calls.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {pctOfTotal.toFixed(1)}%
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {calls > 0 ? (c / calls).toFixed(2) : "0.00"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Usage Patterns & Recommendations */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Peak Usage Times */}
        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Peak Usage Times</h3>
          <div className="space-y-4">
            {/* Add peak usage visualization here */}
          </div>
        </Card>

        {/* Usage Recommendations */}
        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Usage Recommendations</h3>
          <div className="space-y-4">{/* Add recommendations here */}</div>
        </Card>
      </div>
    </>
  );
}
